import { DetectionBoundingPoly } from '@common-modules/mediaDetails/models';
import { notNil } from '@utils';
import { mapValues, sortBy, values } from 'lodash';
import { createSelector } from 'reselect';

import { getBoundingPolyFromGap } from '../helpers/BoundingPoly';
import {
  DetectionGroups,
  selectDetectionGroups,
  selectDetectionGroupsSubset,
  selectDetectionGroupsWithDeleted,
} from './clusters';
import {
  selectDetectionGapsAuto,
  selectDetectionGapsAutoSubset,
  selectDetectionGapsManual,
  selectDetectionGapsManualSubset,
} from './detectionGaps';
import { selectStore } from './view';

import { WorkerState } from '../models';

export const selectFilterParameters = createSelector(
  selectStore,
  (s) => s.filterParameters
);

export const selectDetectionsWithoutInterpolation = createSelector(
  selectDetectionGroupsWithDeleted,
  (groups): ReadonlyArray<DetectionBoundingPoly> => {
    const detections: DetectionBoundingPoly[] = [];
    for (const group of values(groups)) {
      if (!notNil(group)) {
        // not sure this check is really necessary
        continue;
      }

      for (const ss of values(group.subsegments)) {
        for (const o of ss.objects) {
          detections.push({
            ...o,
            subsegmentId: ss.id,
            groupId: group.id,
            clusterId: group.clusterId,
            userLabel: group.userLabel,
          });
        }
      }
    }

    return sortBy(detections, 'startTimeMs');
  }
);

export function selectDetectionsHelper(
  groups: DetectionGroups,
  gapsAuto: WorkerState['detectionGaps'],
  gapsManual: WorkerState['detectionGaps']
) {
  let detections: {
    [groupId: string]: { type: string; series: DetectionBoundingPoly[] | null };
  } = {};

  // let durationClusters = 0;

  detections = mapValues(groups, (g) => {
    const series: DetectionBoundingPoly[] = [];
    for (const ss of values(g.subsegments)) {
      for (const o of ss.objects) {
        series.push({
          ...o,
          subsegmentId: ss.id,
          groupId: g.id,
          clusterId: g.clusterId,
        });
        // durationClusters += o.stopTimeMs - o.startTimeMs;
      }
    }
    // return { series, type: series[0]?.object.type || 'head' };
    return { series, type: g.type };
  });
  // console.log('Total Cluster Duration ', durationClusters / 1000, 'sec');

  // need to update these attributes on gaps - detectionGaps will have stale settings
  // add auto interpolation gaps
  // let durationAuto = 0;
  for (const [groupId, groupGapsAuto] of Object.entries(gapsAuto)) {
    Object.values(groupGapsAuto).forEach(
      (gap) =>
        detections[groupId]?.series?.push(
          getBoundingPolyFromGap(gap, groups[groupId])
        )
      // durationAuto += g.gapLenMs;
    );
  }

  // console.log(
  //   'Total Auto Interpolation Duration ',
  //   durationAuto / 1000,
  //   'sec'
  // );

  // add manual interpolation gaps
  // let durationManual = 0;
  for (const [groupId, groupGapsManual] of Object.entries(gapsManual)) {
    Object.values(groupGapsManual).forEach(
      (gap) =>
        detections[groupId]?.series?.push(
          getBoundingPolyFromGap(gap, groups[groupId])
        )
      // durationManual += g.gapLenMs;
    );
  }

  // console.log(
  //   'Total Manual Interpolation Duration ',
  //   durationManual / 1000,
  //   'sec'
  // );

  return detections;
}

/**
 * All Detections as single array (sorted by startTimeMs)
 */
export const selectDetections = createSelector(
  selectDetectionGroups(),
  selectDetectionGapsAuto(),
  selectDetectionGapsManual(),
  (groups, gapsAuto, gapsManual) => {
    const detections = selectDetectionsHelper(groups, gapsAuto, gapsManual);

    return detections;
  }
);

/**
 * All Detections flattened into a single array (sorted by startTimeMs)
 */
export const selectDetectionsFlattened = createSelector(
  selectDetections,
  (detections): ReadonlyArray<DetectionBoundingPoly> => {
    // currently this output is expect to be combined into single array - later may convert to
    const detectionSeries: DetectionBoundingPoly[] = [];
    Object.values(detections).forEach((group) => {
      if (group.series) {
        detectionSeries.push(...group.series);
      }
    });

    return sortBy(detectionSeries, 'startTimeMs');
  }
);

/**
 * Select Detections Subset
 */

export function* selectDetectionsSubset(groupIds: Array<string>) {
  const groups = yield* selectDetectionGroupsSubset(groupIds);
  const gapsAuto = yield* selectDetectionGapsAutoSubset(groupIds);
  const gapsManual = yield* selectDetectionGapsManualSubset(groupIds);
  return selectDetectionsHelper(groups, gapsAuto, gapsManual);
}
